import { createSelector } from '@reduxjs/toolkit';

import type RootState from 'app-components/utils/redux-store/rootState';

import { billScoutRecordedTransactionsAdapter } from './billScoutRecordedTransactionsSlice';
import {
  BILL_SCOUT_RECORDED_TRANSACTIONS_REDUCER_KEY,
  type BillScoutRecordedTransaction,
  type BillScoutRecordedTransactionsModelState,
  type BillScoutRecordedTransactionsRequestError,
  type BillScoutRecordedTransactionsRequestStatus,
  type BillScoutRecordedTransactionsState,
} from './billScoutRecordedTransactionsTypes';

type StateWithRecordedTransactions = Pick<RootState, typeof BILL_SCOUT_RECORDED_TRANSACTIONS_REDUCER_KEY>;

const selectSlice = (state: StateWithRecordedTransactions): BillScoutRecordedTransactionsState =>
  state[BILL_SCOUT_RECORDED_TRANSACTIONS_REDUCER_KEY];

const selectModel = (
  state: StateWithRecordedTransactions,
  stModelId: string,
): BillScoutRecordedTransactionsModelState | undefined => selectSlice(state).models[stModelId];

const adapterSelectors = billScoutRecordedTransactionsAdapter.getSelectors<StateWithRecordedTransactions>(selectSlice);

export const selectAllRecordedTransactions: (state: StateWithRecordedTransactions) => BillScoutRecordedTransaction[] =
  adapterSelectors.selectAll;

export const selectRecordedTransactionById: (
  state: StateWithRecordedTransactions,
  id: string,
) => BillScoutRecordedTransaction | undefined = adapterSelectors.selectById;

export const selectRecordedTransactionsForModel: (
  state: StateWithRecordedTransactions,
  stModelId: string,
) => BillScoutRecordedTransaction[] = createSelector(
  [selectSlice, (_state: StateWithRecordedTransactions, stModelId: string): string => stModelId],
  (state, stModelId): BillScoutRecordedTransaction[] =>
    (state.models[stModelId]?.associationIds ?? [])
      .map((id) => state.entities[id])
      .filter((association): association is BillScoutRecordedTransaction => association !== undefined),
);

export const selectRecordedTransactionIdsForModel: (
  state: StateWithRecordedTransactions,
  stModelId: string,
) => string[] = createSelector([selectRecordedTransactionsForModel], (associations): string[] => [
  ...new Set(associations.map((association) => association.transactionId)),
]);

export const selectRecordedTransactionAssociationsForTransactionId: (
  state: StateWithRecordedTransactions,
  stModelId: string,
  transactionId: string,
) => BillScoutRecordedTransaction[] = createSelector(
  [
    selectRecordedTransactionsForModel,
    (_state: StateWithRecordedTransactions, _stModelId: string, transactionId: string): string => transactionId,
  ],
  (associations, transactionId): BillScoutRecordedTransaction[] =>
    associations.filter((association) => association.transactionId === transactionId),
);

export const selectHasLoadedForModel = (state: StateWithRecordedTransactions, stModelId: string): boolean =>
  selectModel(state, stModelId)?.hasLoaded ?? false;

export const selectFetchStatusForModel = (
  state: StateWithRecordedTransactions,
  stModelId: string,
): BillScoutRecordedTransactionsRequestStatus => selectModel(state, stModelId)?.fetch.status ?? 'idle';

export const selectMutationStatusForModel = (
  state: StateWithRecordedTransactions,
  stModelId: string,
): BillScoutRecordedTransactionsRequestStatus => selectModel(state, stModelId)?.mutation.status ?? 'idle';

export const selectIsFetchingForModel = (state: StateWithRecordedTransactions, stModelId: string): boolean =>
  selectFetchStatusForModel(state, stModelId) === 'pending';

export const selectIsInitialLoadForModel = (state: StateWithRecordedTransactions, stModelId: string): boolean =>
  !selectHasLoadedForModel(state, stModelId) && selectIsFetchingForModel(state, stModelId);

export const selectIsMutatingForModel = (state: StateWithRecordedTransactions, stModelId: string): boolean =>
  (selectModel(state, stModelId)?.mutation.pendingRequestIds.length ?? 0) > 0;

export const selectMutationErrorForModel = (
  state: StateWithRecordedTransactions,
  stModelId: string,
): BillScoutRecordedTransactionsRequestError | null => selectModel(state, stModelId)?.mutation.error ?? null;

export const selectNeedsReconciliationForModel = (state: StateWithRecordedTransactions, stModelId: string): boolean =>
  selectModel(state, stModelId)?.needsReconciliation ?? false;

export const selectCanMutateForModel = (state: StateWithRecordedTransactions, stModelId: string): boolean =>
  selectHasLoadedForModel(state, stModelId) &&
  selectFetchStatusForModel(state, stModelId) === 'succeeded' &&
  !selectIsMutatingForModel(state, stModelId) &&
  !selectNeedsReconciliationForModel(state, stModelId);
